import React, { useState } from 'react';
import { LogOut } from 'lucide-react';
import LegalService from '../../services/legal.service';
import type { PendingLegalDoc } from '../../types/permissions';
import { describeApiError, hasApiErrorCode } from '../../utils/apiErrors';
import termsDocumentUrl from '../../assets/docs/pelora-terms-of-service.md';
import privacyDocumentUrl from '../../assets/docs/pelora-privacy-policy.md';
import PolicyDocumentDialog, { type PolicyDocument } from './PolicyDocumentDialog';

interface LegalAcceptModalProps {
  /** The ToS / Privacy versions still to accept (from /users/me/permissions pendingLegal). */
  pendingLegal: PendingLegalDoc[];
  /** Re-fetch permissions after acceptance (or a stale-version conflict). */
  onAccepted: () => Promise<void> | void;
  onLogout?: () => void | Promise<void>;
}

const DOC_LABELS: Record<PendingLegalDoc['docType'], { label: string; url: string }> = {
  tos: { label: 'Terms of Service', url: termsDocumentUrl },
  privacy: { label: 'Privacy Policy', url: privacyDocumentUrl },
};

/**
 * Blocking first-login / re-accept dialog for the Terms of Service and Privacy Policy
 * (Phase 5, ACC-02). It covers both new accounts (the sign-up checkbox cannot be
 * recorded before email confirmation) and existing users after a version change.
 */
const LegalAcceptModal: React.FC<LegalAcceptModalProps> = ({ pendingLegal, onAccepted, onLogout }) => {
  const [agreed, setAgreed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Same in-app viewer as the sign-in page links, opened on top of this dialog
  const [policy, setPolicy] = useState<PolicyDocument | null>(null);

  const docs = pendingLegal;

  const handleAccept = async () => {
    if (!agreed || isSubmitting) return;
    setError(null);
    setIsSubmitting(true);
    try {
      await LegalService.accept(docs);
      await onAccepted();
    } catch (err) {
      if (hasApiErrorCode(err, 'LEGAL_VERSION_STALE')) {
        setAgreed(false);
        setError(describeApiError(err));
        await onAccepted();
      } else {
        setError(describeApiError(err, 'Unable to save your acceptance. Please try again.'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="legal-accept-title"
      data-testid="legal-accept-modal"
    >
      <div className="w-full max-w-lg rounded-lg bg-white p-6 text-left shadow-xl">
        <h2 id="legal-accept-title" className="text-xl font-semibold text-slate-900">
          Review and accept to continue
        </h2>
        <p className="mt-2 text-sm text-slate-600">
          Before you continue to Pelora Health, please review and accept the current version of the
          documents below. If you agreed to them when you created your account, accepting here records
          that agreement.
        </p>

        <ul className="mt-4 space-y-2 text-sm">
          {docs.map((doc) => (
            <li key={`${doc.docType}-${doc.version}`} className="flex items-center justify-between rounded border border-slate-200 px-3 py-2">
              <button
                type="button"
                onClick={() => setPolicy({ title: DOC_LABELS[doc.docType].label, url: DOC_LABELS[doc.docType].url })}
                className="font-medium text-blue-600 underline hover:text-blue-700"
              >
                {DOC_LABELS[doc.docType].label}
              </button>
              <span className="text-xs text-slate-500">Version {doc.version}</span>
            </li>
          ))}
        </ul>

        <label className="mt-4 flex items-start gap-3 text-sm text-slate-800">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(event) => setAgreed(event.target.checked)}
            disabled={isSubmitting}
            className="mt-1"
          />
          <span>I have read and agree to the Terms of Service and Privacy Policy.</span>
        </label>

        {error && <p className="mt-3 text-sm text-red-700" role="alert">{error}</p>}

        <div className="mt-6 flex items-center justify-between gap-3">
          {onLogout ? (
            <button
              type="button"
              onClick={() => { void onLogout(); }}
              disabled={isSubmitting}
              className="inline-flex items-center gap-2 rounded px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 disabled:opacity-50"
            >
              <LogOut size={16} aria-hidden="true" />
              Sign out
            </button>
          ) : <span />}
          <button
            type="button"
            onClick={handleAccept}
            disabled={!agreed || isSubmitting || docs.length === 0}
            className="rounded bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-40"
          >
            {isSubmitting ? 'Saving...' : 'Accept and continue'}
          </button>
        </div>
      </div>
      <PolicyDocumentDialog policy={policy} onClose={() => setPolicy(null)} />
    </div>
  );
};

export default LegalAcceptModal;
